import { useState, useEffect, useMemo } from "react";
import { useThemeContext } from "../../theme/ThemeContext";
import { RADIUS, TYPOGRAPHY } from "../../theme/tokens";
import { overlayCentered } from "../../theme/styles";
import { Button } from "../shared";
import { dbLoadEntriesRange } from "../../db/analytics";
import { toMins } from "../../utils/timeUtils";

const TABS = ["Today", "Flights", "Airlines", "Routes", "SCCO", "Trends"];
const RANGES = [
  { label: "7d",  days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function dateRange(appDate, days) {
  const end = appDate;
  const startMs = new Date(appDate).getTime() - (days - 1) * 86400000;
  const start = new Date(startMs).toISOString().slice(0, 10);
  return { start, end };
}

// ── Tab: Today ────────────────────────────────────────────────────────────────
function TodayTab({ entries, appDate, tokens, SA, GLASS }) {
  const stats = useMemo(() => ({
    totalEntries: entries.length,
    paxAtRisk:    entries.reduce((sum, e) => sum + (parseInt(e.ttl_pax, 10) || 0), 0),
    offloads:     entries.filter(e => e.status === "OFFLOADED").length,
    activeHolds:  entries.filter(e => e.will_hold === true).length,
  }), [entries]);

  const mostAtRisk = useMemo(() => {
    const counts = {};
    entries.forEach(e => {
      if (e.airline && e.airline !== "UA") counts[e.airline] = (counts[e.airline] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0] || null;
  }, [entries]);

  const mostDelayed = useMemo(() => {
    let worst = null, worstDelay = -1;
    entries.forEach(e => {
      const eta = e.eta || e.sta, sta = e.sta;
      if (!eta || !sta) return;
      const delay = toMins(eta) - toMins(sta);
      if (delay > worstDelay) { worstDelay = delay; worst = { flight: e.inFlt || "—", delay }; }
    });
    return worst;
  }, [entries]);

  const sccoWorkload = useMemo(() => {
    const map = {};
    entries.forEach(e => {
      const name = e.scco_name || "Unknown";
      if (!map[name]) map[name] = { entries: 0, pax: 0 };
      map[name].entries++;
      map[name].pax += parseInt(e.ttl_pax, 10) || 0;
    });
    return Object.entries(map).map(([name, d]) => ({ name, ...d })).sort((a, b) => b.entries - a.entries);
  }, [entries]);

  const pillStyle = {
    background: GLASS.card, border: `1px solid ${GLASS.border}`,
    borderRadius: RADIUS.lg, padding: "12px 16px",
    flex: "1 1 calc(25% - 8px)", minWidth: 80, textAlign: "center",
  };
  const labelStyle = {
    fontSize: TYPOGRAPHY.tiny, color: SA.silver, textTransform: "uppercase",
    letterSpacing: "0.08em", marginBottom: 4, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
  };
  const valueStyle = { fontSize: TYPOGRAPHY.h2, color: SA.white, fontWeight: 700 };

  return (
    <div>
      <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.08em",
        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", marginBottom: 12 }}>
        {appDate} · TODAY'S OPERATIONS
      </div>

      {/* KPI pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        <div style={pillStyle}>
          <div style={labelStyle}>Entries</div>
          <div style={valueStyle}>{stats.totalEntries}</div>
        </div>
        <div style={pillStyle}>
          <div style={labelStyle}>Pax at Risk</div>
          <div style={{ ...valueStyle, color: stats.paxAtRisk > 0 ? tokens.warning : SA.white }}>{stats.paxAtRisk}</div>
        </div>
        <div style={pillStyle}>
          <div style={labelStyle}>Offloads</div>
          <div style={{ ...valueStyle, color: stats.offloads > 0 ? tokens.danger : SA.white }}>{stats.offloads}</div>
        </div>
        <div style={pillStyle}>
          <div style={labelStyle}>Active Holds</div>
          <div style={{ ...valueStyle, color: stats.activeHolds > 0 ? tokens.warning : SA.white }}>{stats.activeHolds}</div>
        </div>
      </div>

      {/* Most at-risk airline */}
      <div style={{ background: GLASS.card, border: `1px solid ${GLASS.border}`, borderRadius: RADIUS.md, padding: "12px 14px", marginBottom: 10 }}>
        <div style={labelStyle}>Most At-Risk Airline</div>
        {mostAtRisk
          ? <div style={{ color: SA.white, fontSize: TYPOGRAPHY.body, fontWeight: 600, marginTop: 2 }}>
              {mostAtRisk[0]} <span style={{ color: SA.silver, fontWeight: 400 }}>· {mostAtRisk[1]} connection{mostAtRisk[1] !== 1 ? "s" : ""}</span>
            </div>
          : <div style={{ color: SA.silver, fontSize: TYPOGRAPHY.caption, marginTop: 2 }}>No data</div>
        }
      </div>

      {/* Most delayed inbound */}
      <div style={{ background: GLASS.card, border: `1px solid ${GLASS.border}`, borderRadius: RADIUS.md, padding: "12px 14px", marginBottom: 10 }}>
        <div style={labelStyle}>Most Delayed Inbound</div>
        {mostDelayed && mostDelayed.delay > 0
          ? <div style={{ color: SA.white, fontSize: TYPOGRAPHY.body, fontWeight: 600, marginTop: 2 }}>
              {mostDelayed.flight} <span style={{ color: tokens.warning, fontWeight: 400 }}>· +{mostDelayed.delay} min</span>
            </div>
          : <div style={{ color: SA.silver, fontSize: TYPOGRAPHY.caption, marginTop: 2 }}>No delays</div>
        }
      </div>

      {/* SCCO workload */}
      <div style={{ background: GLASS.card, border: `1px solid ${GLASS.border}`, borderRadius: RADIUS.md, padding: "12px 14px" }}>
        <div style={{ ...labelStyle, marginBottom: 8 }}>SCCO Workload</div>
        {sccoWorkload.length === 0
          ? <div style={{ color: SA.silver, fontSize: TYPOGRAPHY.caption }}>No entries</div>
          : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: TYPOGRAPHY.caption }}>
              <thead>
                <tr>
                  {["Name", "Entries", "Pax"].map(h => (
                    <th key={h} style={{ color: SA.silver, textAlign: h === "Name" ? "left" : "right", paddingBottom: 4, fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sccoWorkload.map(row => (
                  <tr key={row.name}>
                    <td style={{ color: SA.white, paddingTop: 3 }}>{row.name}</td>
                    <td style={{ color: SA.white, textAlign: "right", paddingTop: 3 }}>{row.entries}</td>
                    <td style={{ color: SA.silver, textAlign: "right", paddingTop: 3 }}>{row.pax}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  );
}

// ── Tab: Flights ──────────────────────────────────────────────────────────────
function FlightsTab({ rows, tokens, SA, GLASS }) {
  const data = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const key = r.in_flt || "";
      if (!key.trim()) return;
      if (!map[key]) map[key] = { key, airline: r.in_airline || "", seen: 0, late: 0, delaySum: 0 };
      map[key].seen++;
      const eta = r.eta || r.sta;
      const sta = r.sta;
      if (eta && sta) {
        const delay = toMins(eta) - toMins(sta);
        if (delay > 0) { map[key].late++; map[key].delaySum += delay; }
      }
    });
    return Object.values(map)
      .sort((a, b) => b.delaySum / Math.max(b.seen, 1) - a.delaySum / Math.max(a.seen, 1))
      .slice(0, 10)
      .map(d => ({
        ...d,
        latePct: d.seen > 0 ? Math.round((d.late / d.seen) * 100) : 0,
        avgDelay: d.late > 0 ? Math.round(d.delaySum / d.late) : 0,
      }));
  }, [rows]);

  if (data.length === 0) return <EmptyMsg SA={SA} />;
  return (
    <DataTable
      SA={SA} GLASS={GLASS}
      cols={["Flight", "Airline", "Seen", "Late%", "Avg Delay"]}
      rows={data.map(d => [
        d.key,
        d.airline,
        d.seen,
        <span style={{ color: d.latePct >= 50 ? tokens.danger : d.latePct >= 25 ? tokens.warning : SA.silver }}>{d.latePct}%</span>,
        d.avgDelay > 0 ? <span style={{ color: tokens.warning }}>+{d.avgDelay}m</span> : "—",
      ])}
    />
  );
}

// ── Tab: Airlines ─────────────────────────────────────────────────────────────
function AirlinesTab({ rows, tokens, SA, GLASS }) {
  const data = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const key = r.airline || "Unknown";
      if (key === "UA") return;
      if (!map[key]) map[key] = { airline: key, entries: 0, pax: 0, offloads: 0, bagsExp: 0, ttlBags: 0 };
      map[key].entries++;
      map[key].pax += parseInt(r.ttl_pax, 10) || 0;
      if (r.status === "OFFLOADED") map[key].offloads++;
      map[key].bagsExp += parseInt(r.bags_exp, 10) || 0;
      map[key].ttlBags += parseInt(r.ttl_bags, 10) || 0;
    });
    return Object.values(map)
      .sort((a, b) => b.entries - a.entries)
      .map(d => ({
        ...d,
        offloadPct: d.entries > 0 ? Math.round((d.offloads / d.entries) * 100) : 0,
        bagRecovery: d.ttlBags > 0 ? Math.round((d.bagsExp / d.ttlBags) * 100) : null,
      }));
  }, [rows]);

  if (data.length === 0) return <EmptyMsg SA={SA} />;
  return (
    <DataTable
      SA={SA} GLASS={GLASS}
      cols={["Airline", "Entries", "Pax", "Offload%", "Bag Rec%"]}
      rows={data.map(d => [
        d.airline,
        d.entries,
        d.pax,
        <span style={{ color: d.offloadPct >= 25 ? tokens.danger : SA.silver }}>{d.offloadPct}%</span>,
        d.bagRecovery !== null ? `${d.bagRecovery}%` : "—",
      ])}
    />
  );
}

// ── Tab: Routes ───────────────────────────────────────────────────────────────
function RoutesTab({ rows, tokens, SA, GLASS }) {
  const data = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const origin = r.origin || "?";
      const dest = r.dest || "?";
      const key = `${origin}→${dest}`;
      if (!map[key]) map[key] = { key, entries: 0, pax: 0, withStatus: 0 };
      map[key].entries++;
      map[key].pax += parseInt(r.ttl_pax, 10) || 0;
      if (r.status) map[key].withStatus++;
    });
    return Object.values(map)
      .sort((a, b) => b.entries - a.entries)
      .slice(0, 15)
      .map(d => ({
        ...d,
        atRiskPct: d.entries > 0 ? Math.round((d.withStatus / d.entries) * 100) : 0,
      }));
  }, [rows]);

  if (data.length === 0) return <EmptyMsg SA={SA} />;
  return (
    <DataTable
      SA={SA} GLASS={GLASS}
      cols={["Route", "Entries", "Pax", "At-Risk%"]}
      rows={data.map(d => [
        <span style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.caption }}>{d.key}</span>,
        d.entries,
        d.pax,
        <span style={{ color: d.atRiskPct >= 50 ? tokens.danger : d.atRiskPct >= 25 ? tokens.warning : SA.silver }}>{d.atRiskPct}%</span>,
      ])}
    />
  );
}

// ── Tab: SCCO ─────────────────────────────────────────────────────────────────
function SCCOTab({ rows, SA, GLASS }) {
  const data = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!r.scco_name) return;
      const key = r.scco_name;
      if (!map[key]) map[key] = { name: key, entries: 0, pax: 0, offloads: 0, holds: 0 };
      map[key].entries++;
      map[key].pax += parseInt(r.ttl_pax, 10) || 0;
      if (r.status === "OFFLOADED") map[key].offloads++;
      if (r.will_hold === true) map[key].holds++;
    });
    return Object.values(map).sort((a, b) => b.entries - a.entries);
  }, [rows]);

  if (data.length === 0) return <EmptyMsg SA={SA} />;
  return (
    <DataTable
      SA={SA} GLASS={GLASS}
      cols={["SCCO", "Entries", "Pax", "Offloads", "Holds"]}
      rows={data.map(d => [d.name, d.entries, d.pax, d.offloads, d.holds])}
    />
  );
}

// ── Tab: Trends ───────────────────────────────────────────────────────────────
function TrendsTab({ rows, SA, GLASS, tokens }) {
  const data = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const d = r.date || "?";
      if (!map[d]) map[d] = { date: d, entries: 0, pax: 0, offloads: 0, holds: 0 };
      map[d].entries++;
      map[d].pax += parseInt(r.ttl_pax, 10) || 0;
      if (r.status === "OFFLOADED") map[d].offloads++;
      if (r.will_hold === true) map[d].holds++;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  const maxEntries = useMemo(() => Math.max(...data.map(d => d.entries), 1), [data]);

  if (data.length === 0) return <EmptyMsg SA={SA} />;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: TYPOGRAPHY.caption }}>
        <thead>
          <tr>
            {["Date", "Entries", "Bar", "Pax", "Offloads", "Holds"].map(h => (
              <th key={h} style={{ color: SA.silver, textAlign: h === "Date" || h === "Bar" ? "left" : "right", paddingBottom: 6, fontWeight: 500, paddingRight: h !== "Holds" ? 12 : 0 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map(row => (
            <tr key={row.date} style={{ borderTop: `1px solid ${GLASS.border}` }}>
              <td style={{ color: SA.silver, paddingTop: 5, paddingRight: 12, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>{row.date.slice(5)}</td>
              <td style={{ color: SA.white, textAlign: "right", paddingTop: 5, paddingRight: 12 }}>{row.entries}</td>
              <td style={{ paddingTop: 5, paddingRight: 12, width: 80 }}>
                <div style={{ height: 6, borderRadius: 3, background: tokens.primary || "#4a7cf7", width: `${Math.round((row.entries / maxEntries) * 100)}%`, minWidth: 2 }} />
              </td>
              <td style={{ color: SA.silver, textAlign: "right", paddingTop: 5, paddingRight: 12 }}>{row.pax}</td>
              <td style={{ color: row.offloads > 0 ? tokens.danger : SA.silver, textAlign: "right", paddingTop: 5, paddingRight: 12 }}>{row.offloads}</td>
              <td style={{ color: row.holds > 0 ? tokens.warning : SA.silver, textAlign: "right", paddingTop: 5 }}>{row.holds}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────
function EmptyMsg({ SA }) {
  return <div style={{ color: SA.silver, fontSize: TYPOGRAPHY.caption, textAlign: "center", padding: "24px 0" }}>No data for this range.</div>;
}

function DataTable({ cols, rows, SA, GLASS }) {
  const alignRight = (_, i) => i > 0;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: TYPOGRAPHY.caption }}>
        <thead>
          <tr>
            {cols.map((h, i) => (
              <th key={h} style={{ color: SA.silver, textAlign: alignRight(h, i) ? "right" : "left", paddingBottom: 6, fontWeight: 500, paddingRight: i < cols.length - 1 ? 12 : 0 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ borderTop: `1px solid ${GLASS.border}` }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ color: SA.white, textAlign: alignRight(cell, ci) ? "right" : "left", paddingTop: 5, paddingRight: ci < row.length - 1 ? 12 : 0 }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
export function OpsIntelModal({ entries, appDate, onClose }) {
  const { SA, GLASS, tokens } = useThemeContext();
  const [tab, setTab] = useState("Today");
  const [range, setRange] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tab === "Today") return;
    let cancelled = false;
    setLoading(true);
    const { start, end } = dateRange(appDate, range);
    dbLoadEntriesRange(start, end).then(data => {
      if (!cancelled) { setRows(data); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [appDate, range, tab]);

  const subtitle = tab === "Today"
    ? "Today's operations"
    : `Last ${range} days · ${rows.length} connections`;

  const tabContent = useMemo(() => {
    if (tab === "Today") return <TodayTab entries={entries} appDate={appDate} tokens={tokens} SA={SA} GLASS={GLASS} />;
    if (loading) return null;
    const props = { rows, tokens, SA, GLASS };
    switch (tab) {
      case "Flights":  return <FlightsTab  {...props} />;
      case "Airlines": return <AirlinesTab {...props} />;
      case "Routes":   return <RoutesTab   {...props} />;
      case "SCCO":     return <SCCOTab     {...props} />;
      case "Trends":   return <TrendsTab   {...props} />;
      default:         return null;
    }
  }, [tab, rows, loading, entries, appDate, tokens, SA, GLASS]);

  return (
    <div style={{ ...overlayCentered, zIndex: 3100 }} onClick={onClose}>
      <div
        style={{
          ...GLASS.modal,
          borderRadius: RADIUS.xl,
          padding: "24px 20px",
          width: "min(600px, 95vw)",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: TYPOGRAPHY.h3, fontWeight: 700, color: SA.white, letterSpacing: "0.04em" }}>
              Ops Intelligence
            </div>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 2 }}>
              {subtitle}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: SA.silver, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 0 }}>✕</button>
        </div>

        {/* Range toggle — hidden on Today tab */}
        {tab !== "Today" && (
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {RANGES.map(r => (
              <Button key={r.days} variant="pill" active={range === r.days} onClick={() => setRange(r.days)} size="sm">
                {r.label}
              </Button>
            ))}
          </div>
        )}

        {/* Tab bar */}
        <div style={{ display: "flex", gap: 4, marginBottom: 14, overflowX: "auto" }}>
          {TABS.map(t => (
            <Button key={t} variant="pill" active={tab === t} onClick={() => setTab(t)} size="sm">
              {t}
            </Button>
          ))}
        </div>

        {/* Content */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {tab !== "Today" && loading
            ? <div style={{ color: SA.silver, fontSize: TYPOGRAPHY.caption, textAlign: "center", padding: "32px 0" }}>Loading…</div>
            : tabContent
          }
        </div>
      </div>
    </div>
  );
}
